import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import api from '../../lib/api';
import { invitationUrlSegment } from '../../lib/slug';
import { Invitation } from '../../types';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import DataTable from '../../components/ui/DataTable';
import { formatDate } from '../../lib/utils';

export default function KOLInvitations() {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadInvitations();
  }, [statusFilter]);

  const loadInvitations = async () => {
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await api.get('/invitations', { params });
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      PENDING: 'warning',
      ACCEPTED: 'success',
      REJECTED: 'destructive',
      MODIFICATION_REQUESTED: 'secondary',
      WITHDRAWN: 'outline',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const columns = [
    {
      header: 'Job',
      accessor: (row: Invitation) => (
        <div>
          <div className="font-medium text-glass-text">{row.job?.title}</div>
          <div className="text-sm text-glass-text-3">{row.job?.brandOrg?.name}</div>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: (row: Invitation) => (
        <div className="space-y-1">
          {getStatusBadge(row.status)}
          {row.isShortlisted && <Badge variant="success" className="ml-2">Shortlisted</Badge>}
          {row.isApprovedForProduction && <Badge variant="success" className="ml-2">Approved</Badge>}
        </div>
      ),
    },
    {
      header: 'Received',
      accessor: (row: Invitation) => formatDate(row.createdAt),
    },
  ];

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Invitations</h1>
        <p className="text-glass-text-2 text-sm mt-1">View and respond to job invitations</p>
      </div>

      <div className="glass-card p-4">
        <div className="flex items-center gap-4">
          <Mail className="w-5 h-5 text-glass-text-3" />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-48"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="REJECTED">Rejected</option>
            <option value="MODIFICATION_REQUESTED">Modification Requested</option>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <DataTable
          data={invitations}
          columns={columns}
          onRowClick={(inv) => navigate(`/kol/invitations/${encodeURIComponent(invitationUrlSegment(inv))}`)}
          isLoading={loading}
          emptyMessage="No invitations yet. When agencies send you job invitations, they'll appear here."
        />
      </div>
    </div>
  );
}
